import React from "react";
import "./SystemReceiptPdfTemplate.scss";

export const SystemReceiptPdfTemplate = ({ payment, invoice, settings, companyDetails }) => {
  const { header, displayBankDetails, bankDetails } = settings;

  // Destructure props from companyDetails (company object)
  const {
    companyName,
    address,
    state,
    zip,
    gstin = "24AACCL0249H1ZU",
  } = companyDetails || {};

  // Destructure props from payment object
  const {
    recieptId,
    paymentDate,
    amount,
    paymentType,
    transactionId,
    chequeNumber,
    chequeDate,
    bankName,
    upiId,
    remarks,
  } = payment || {};

  // Get client name and invoice number from invoice
  const clientName = invoice?.clientName || companyName || "N/A";
  const invoiceNumber = invoice?.invoiceNumber || "N/A";

  // Helper function to handle image URLs
  const getDisplayUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("data:")) return url;
    if (url.startsWith("http")) return url;
    if (url.startsWith("/static/") || url.startsWith("/assets/")) return url;
    return `${process.env.REACT_APP_BACKEND_MAIN_URL_PATH}ftp/view/${url}`;
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
      .replace(/ /g, "-");
  };

  // Helper function to convert number to words (Indian numbering system)
  const numberToWords = (num) => {
    if (!num || isNaN(num)) return "Zero";
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

    const convertLessThanThousand = (n) => {
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertLessThanThousand(n % 100) : '');
    };

    const [integerPart, decimalPart] = num.toString().split('.');
    const intNum = parseInt(integerPart);
    
    if (intNum === 0) return 'Zero Rupees Only';

    let result = '';
    
    // Crores
    if (intNum >= 10000000) {
      result += convertLessThanThousand(Math.floor(intNum / 10000000)) + ' Crore ';
    }
    
    // Lakhs
    const remainder1 = intNum % 10000000;
    if (remainder1 >= 100000) {
      result += convertLessThanThousand(Math.floor(remainder1 / 100000)) + ' Lakh ';
    }
    
    // Thousands
    const remainder2 = remainder1 % 100000;
    if (remainder2 >= 1000) {
      result += convertLessThanThousand(Math.floor(remainder2 / 1000)) + ' Thousand ';
    }
    
    // Hundreds
    const remainder3 = remainder2 % 1000;
    if (remainder3 > 0) {
      result += convertLessThanThousand(remainder3);
    }

    result = result.trim() + ' Rupees';
    
    if (decimalPart && parseInt(decimalPart) > 0) {
      result += ' and ' + convertLessThanThousand(parseInt(decimalPart.padEnd(2, '0'))) + ' Paise';
    }
    
    return result + ' Only';
  };

  return (
    <div className="receipt-pdf system-receipt-layout">
      {/* 1. HEADER: Simple Image */}
      <div className="simple-header req-pad">
        {header ? (
          <img
            src={getDisplayUrl(header)}
            alt="Header"
            className="receipt-header-image"
          />
        ) : (
          <p>Header not available</p>
        )}
      </div>

      {/* 2. TITLE */}
      <div className="receipt-title req-pad">
        <h1>PAYMENT RECEIPT</h1>
      </div>

      {/* 3. INFO BOX: Receipt Details */}
      <div className="single-block-info req-pad">
        <p>
          <strong>Receipt No.:</strong> {recieptId || "N/A"}
        </p>
        <p>
          <strong>Invoice No.:</strong> {invoiceNumber}
        </p>
        <p>
          <strong>Date:</strong> {formatDate(paymentDate)}
        </p>
        <p>
          <strong>Received From:</strong> {clientName}
        </p>
        <p>
          <strong>Company Address:</strong> {address || "N/A"}
        </p>
        <p style={{lineHeight: "1.6", marginBottom: "15px"}}>
          <strong>State:</strong> {state || "N/A"}
          &nbsp;&nbsp;&nbsp;
          <strong>Pincode:</strong> {zip || "N/A"}
        </p>
        <p>

        </p>
      </div>

      {/* 4. PAYMENT DETAILS TABLE */}
      <table className="receipt-table system-receipt-table req-pad">
        <thead>
          <tr>
            <th>DESCRIPTION</th>
            <th>AMOUNT</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <strong>Payment Received</strong>
              <br />
              <span style={{ fontSize: "0.9em" }}>
                Payment Mode: {paymentType || "N/A"}
              </span>
              {transactionId && (
                <>
                  <br />
                  <span style={{ fontSize: "0.9em" }}>
                    Transaction ID: {transactionId}
                  </span>
                </>
              )}
              {chequeNumber && (
                <>
                  <br />
                  <span style={{ fontSize: "0.9em" }}>
                    Cheque No.: {chequeNumber}
                    {chequeDate && ` | Date: ${formatDate(chequeDate)}`}
                  </span>
                </>
              )}
              {bankName && (
                <>
                  <br />
                  <span style={{ fontSize: "0.9em" }}>
                    Bank: {bankName}
                  </span>
                </>
              )}
              {upiId && (
                <>
                  <br />
                  <span style={{ fontSize: "0.9em" }}>
                    UPI ID: {upiId}
                  </span>
                </>
              )}
              {remarks && (
                <>
                  <br />
                  <span style={{ fontSize: "0.9em" }}>
                    Remarks: {remarks}
                  </span>
                </>
              )}
            </td>
            <td>Rs. {amount ? amount.toFixed(2) : "0.00"}/-</td>
          </tr>

          {/* Amount in Words Row */}
          <tr>
            <td colSpan="2">
              <strong>Amount in Words:</strong>{" "}
              {numberToWords(amount)}
            </td>
          </tr>

          {/* Total Row */}
          <tr className="grand-total-row">
            <td style={{ textAlign: "right" }}>
              <strong>TOTAL AMOUNT RECEIVED :</strong>
            </td>
            <td>
              <strong>Rs. {amount ? amount.toFixed(2) : "0.00"}/-</strong>
            </td>
          </tr>
        </tbody>
      </table>

      {/* 5. FOOTER */}
      <div className="simple-footer req-pad">
        {displayBankDetails && bankDetails && (
          <div className="bank-details-section">
            <p>
              Payment received for invoice services from{" "}
              <strong>LEVONS TECHNOLOGIES PVT. LTD.</strong>
            </p>
            {/* <p>
              <strong>Company GSTIN:</strong> {gstin}
            </p> */}
          </div>
        )}
        <div className="footer-message">
          <p className="system-generated-note">
            This is a system generated receipt
          </p>
        </div>
      </div>
    </div>
  );
};
